import { getSDK, type Message } from '@littlebird/sdk';
import { useMatchRoute } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';

import { XStack, YStack } from '@/components/ui';
import { loaderPortalId } from '@/components/ui/logo-loader';
import { Markdown } from '@/components/ui/smoothdown';
import { ActionCardItem } from '@/features/action-cards/components/ActionCardItem';
import { WikiUserAvatar } from '@/features/people-wiki/components/user-avatar';
import { useWikiPersonOfUser } from '@/features/people-wiki/use-wiki-person-of-user';
import { cn } from '@/lib/utils';

import type { HivemindIncomingSender } from './hivemind-conversations-messages';

type HivemindIncomingMessageProps = {
  readonly threadId: string;
  readonly message: Message;
  readonly messageIndex: number;
  readonly sender: HivemindIncomingSender;
  // The side padding of the surrounding messages, so this one stays in their column
  readonly className?: string;
};

/** The cards opened for the message are anchored to it until a draft moves them to the agent's reply */
const IncomingReplyCards = observer(function IncomingReplyCards({
  messageId,
  className,
}: {
  readonly messageId: string;
  readonly className?: string;
}) {
  // Cards arrive by sync under the same MobX store, so React Compiler must not cache this lookup
  'use no memo';
  const replyCards =
    getSDK().stores.actionCards.getByAssistantMessageId(messageId);
  if (replyCards.length === 0) return null;
  return (
    <YStack className={cn('mb-6 gap-3', className)}>
      {replyCards.map((card) => (
        <ActionCardItem key={card.id} card={card} />
      ))}
    </YStack>
  );
});

export const HivemindIncomingMessage = observer(
  function HivemindIncomingMessage({
    threadId,
    message,
    messageIndex,
    sender,
    className,
  }: HivemindIncomingMessageProps) {
    const matchRoute = useMatchRoute();
    // A shared chat has no signed-in user, so no cards and no SDK
    const isSharedChat = !!matchRoute({ to: '/shared-chat/$id' });
    const senderName =
      useWikiPersonOfUser(sender.senderId)?.name ?? sender.senderName;

    return (
      <>
        <div
          data-message-index={messageIndex}
          data-message-id={message.id}
          data-role="incoming"
          role="article"
          aria-label={`Message from ${senderName}`}
          id={`message-${messageIndex}`}
          className={cn(
            'relative mb-8 flex w-full flex-col items-start gap-1.5',
            className
          )}
        >
          <div
            id={loaderPortalId(message.id)}
            className="pointer-events-none absolute -bottom-7 left-0 size-6"
          />
          <XStack className="items-center gap-2">
            <WikiUserAvatar
              userId={sender.senderId}
              name={senderName}
              className="size-6 text-[0.5625rem]"
            />
            <span className="text-sm font-medium text-foreground">
              {senderName}
            </span>
            <span className="text-xs text-foreground-subtle">
              {sender.sentAutomatically
                ? 'Sent automatically'
                : 'via Littlebird'}
            </span>
          </XStack>
          {/* The user's own bubble shape on the other side, a step darker, so it is told apart from it and from the agent's text */}
          <div
            data-allow-selection
            className="max-w-[80%] break-words rounded-xl bg-surface-active px-3.5 py-1.5 text-foreground"
          >
            <Markdown
              id={`thread-${threadId}-incoming-message-${message.id}`}
              content={message.body}
            />
          </div>
        </div>
        {!isSharedChat && (
          <IncomingReplyCards messageId={message.id} className={className} />
        )}
      </>
    );
  }
);
